import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { apiClient } from '../apiTools/apiClient';
import { allRoutes } from '../constants/config';

const { API_LITGROUPS } = { ...allRoutes };

// ============================================================
// URL helpers (litGroup-scoped cast/merge tooling)
// ============================================================
const castUrl = (litGroupUuid) => `${API_LITGROUPS}/${litGroupUuid}/characters`;
const mergeUrl = (litGroupUuid) => `${API_LITGROUPS}/${litGroupUuid}/characters/merge`;
const unmergeUrl = (litGroupUuid) => `${API_LITGROUPS}/${litGroupUuid}/characters/unmerge`;
const rejectUrl = (litGroupUuid) => `${API_LITGROUPS}/${litGroupUuid}/characters/merge-reject`;
const unrejectUrl = (litGroupUuid) => `${API_LITGROUPS}/${litGroupUuid}/characters/merge-unreject`;
const dismissUrl = (litGroupUuid) => `${API_LITGROUPS}/${litGroupUuid}/characters/dismiss`;
const contextUrl = (litGroupUuid, characterUuid) =>
  `${API_LITGROUPS}/${litGroupUuid}/characters/${characterUuid}/context`;

// Preserve the server's 409 { error, blocked:[...] } payload so the UI can name the
// protected (recorded/assigned) characters that blocked the operation.
const rejectValue = (error) =>
  error.response?.data || { error: error.message || 'Request failed' };

// ============================================================
// Async Thunks
// ============================================================

/** The full cast of a published LitGroup with dialogue counts + recorded/assigned flags. */
export const fetchLitGroupCast = createAsyncThunk(
  'litGroupCast/fetch',
  async (litGroupUuid, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(castUrl(litGroupUuid));
      // books: the LitGroup's books, so the cast list can offer a book filter with real titles.
      return {
        litGroupUuid,
        characters: response.data.characters || [],
        books: response.data.books || [],
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error));
    }
  }
);

/** Fold variant characters into a canonical (persists aliases, deletes variant subtrees). */
export const mergeCharacters = createAsyncThunk(
  'litGroupCast/merge',
  async ({ litGroupUuid, canonicalUuid, mergeUuids }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        mergeUrl(litGroupUuid),
        { canonicalUuid, mergeUuids },
        {},
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(rejectValue(error));
    }
  }
);

/** Revive tombstoned merge targets (the inverse of merge). */
export const unmergeCharacters = createAsyncThunk(
  'litGroupCast/unmerge',
  async ({ litGroupUuid, targetUuids, batchId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        unmergeUrl(litGroupUuid),
        { targetUuids, batchId },
        {},
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(rejectValue(error));
    }
  }
);

/**
 * "Not the same" — record that a character is NOT a variant of the ones it was offered beside.
 *
 * ⭐ `againstUuids` IS A LIST, AND THAT IS THE POINT. Refusing a member against only the canonical
 * leaves it able to rejoin the same group through a third member that legitimately contains both,
 * because union-find is transitive and a pair veto is not. So the caller sends every other member
 * present, which is what "not one of these" actually means.
 */
export const rejectMerge = createAsyncThunk(
  'litGroupCast/rejectMerge',
  async ({ litGroupUuid, uuid, againstUuids }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        rejectUrl(litGroupUuid),
        { uuid, againstUuids },
        {},
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(rejectValue(error));
    }
  }
);

/** Undo a "not the same" — the counterpart of rejectMerge, as unmerge is to merge. */
export const unrejectMerge = createAsyncThunk(
  'litGroupCast/unrejectMerge',
  async ({ litGroupUuid, uuid, againstUuids }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        unrejectUrl(litGroupUuid),
        { uuid, againstUuids },
        {},
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(rejectValue(error));
    }
  }
);

/** Surrounding-text passages for one character (lazy context for a merge decision). */
export const fetchCharacterContext = createAsyncThunk(
  'litGroupCast/fetchContext',
  async ({ litGroupUuid, characterUuid, q = '', samples }, { rejectWithValue }) => {
    try {
      // A search term windows the panel around MATCHES instead of around the first few occurrences.
      // Without it the sample is always hits[0..3], so the Narrator's 3,855 lines could only ever
      // show the opening of one book — the panel was structurally incapable of reaching line 2,000.
      // `samples` is the other half: reading a character's lines rather than confirming an identity.
      // ⚠️ apiClient is NOT axios — get(url, pathParams, queryParams, config). The query string is
      // the THIRD argument; putting `{ params: … }` second drops it silently into path substitution.
      const queryParams = {};
      if (q) queryParams.q = q;
      if (samples) queryParams.samples = samples;
      const response = await apiClient.get(contextUrl(litGroupUuid, characterUuid), {}, queryParams);
      return {
        characterUuid,
        q,
        samples: samples || 3,
        passages: response.data.passages || [],
        total: response.data.total || 0,
      };
    } catch (error) {
      return rejectWithValue({ characterUuid, ...rejectValue(error) });
    }
  }
);

/** Remove spurious (non-character) entries from the cast. */
export const dismissCharacters = createAsyncThunk(
  'litGroupCast/dismiss',
  async ({ litGroupUuid, uuids }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        dismissUrl(litGroupUuid),
        { uuids },
        {},
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(rejectValue(error));
    }
  }
);

// ============================================================
// Slice
// ============================================================
const initialState = {
  litGroupUuid: null,
  characters: [],
  // [{ uuid, title }] — the group's books, for the cast list's book filter.
  books: [],
  loading: { fetch: false, merge: false, unmerge: false, dismiss: false },
  error: null,       // plain message for the current failure
  blocked: null,     // [{ uuid, name, recorded, assigned }] from a 409 guard
  lastResult: null,  // { kind: 'merge'|'dismiss', ...payload } for a success toast
  // Lazy per-character surrounding-context cache, keyed by character uuid:
  //   { [uuid]: { loading, passages: [{bookTitle, targetIndex, lines:[...]}], total, error } }
  contextByChar: {},
};

const litGroupCastSlice = createSlice({
  name: 'litGroupCast',
  initialState,
  reducers: {
    clearCastFeedback(state) {
      state.error = null;
      state.blocked = null;
      state.lastResult = null;
    },
    resetCast() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchLitGroupCast.pending, (state) => {
        state.loading.fetch = true;
        state.error = null;
      })
      .addCase(fetchLitGroupCast.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.litGroupUuid = action.payload.litGroupUuid;
        state.characters = action.payload.characters;
        state.books = action.payload.books || [];
        // A fresh cast (possibly post-merge) invalidates the context cache.
        state.contextByChar = {};
      })
      .addCase(fetchLitGroupCast.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = action.payload?.error || 'Failed to load cast';
      })
      // merge
      .addCase(mergeCharacters.pending, (state) => {
        state.loading.merge = true;
        state.error = null;
        state.blocked = null;
      })
      .addCase(mergeCharacters.fulfilled, (state, action) => {
        state.loading.merge = false;
        state.lastResult = { kind: 'merge', ...action.payload };
      })
      .addCase(mergeCharacters.rejected, (state, action) => {
        state.loading.merge = false;
        state.error = action.payload?.error || 'Merge failed';
        state.blocked = action.payload?.blocked || null;
      })
      // unmerge
      .addCase(unmergeCharacters.pending, (state) => {
        state.loading.unmerge = true;
        state.error = null;
        state.blocked = null;
      })
      .addCase(unmergeCharacters.fulfilled, (state, action) => {
        state.loading.unmerge = false;
        state.lastResult = { kind: 'unmerge', ...action.payload };
      })
      .addCase(unmergeCharacters.rejected, (state, action) => {
        state.loading.unmerge = false;
        state.error = action.payload?.error || 'Unmerge failed';
      })
      // surrounding context (lazy, per character)
      .addCase(fetchCharacterContext.pending, (state, action) => {
        const uuid = action.meta.arg.characterUuid;
        state.contextByChar[uuid] = {
          ...(state.contextByChar[uuid] || {}),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchCharacterContext.fulfilled, (state, action) => {
        const { characterUuid, passages, total, q, samples } = action.payload;
        state.contextByChar[characterUuid] = {
          loading: false, passages, total, error: null, q: q || '', samples: samples || 3,
        };
      })
      .addCase(fetchCharacterContext.rejected, (state, action) => {
        const uuid = action.payload?.characterUuid || action.meta.arg.characterUuid;
        state.contextByChar[uuid] = {
          loading: false,
          passages: [],
          total: 0,
          error: action.payload?.error || 'Failed to load context',
        };
      })
      // dismiss
      .addCase(dismissCharacters.pending, (state) => {
        state.loading.dismiss = true;
        state.error = null;
        state.blocked = null;
      })
      .addCase(dismissCharacters.fulfilled, (state, action) => {
        state.loading.dismiss = false;
        state.lastResult = { kind: 'dismiss', ...action.payload };
      })
      .addCase(dismissCharacters.rejected, (state, action) => {
        state.loading.dismiss = false;
        state.error = action.payload?.error || 'Dismiss failed';
        state.blocked = action.payload?.blocked || null;
      });
  },
});

export const { clearCastFeedback, resetCast } = litGroupCastSlice.actions;

// ============================================================
// Selectors
// ============================================================
export const selectCast = (state) => state.litGroupCast.characters;
export const selectCastLoading = (state) => state.litGroupCast.loading;
export const selectCastError = (state) => state.litGroupCast.error;
export const selectCastBlocked = (state) => state.litGroupCast.blocked;
/** The group's books, for the cast list's book filter: [{ uuid, title }]. */
export const selectCastBooks = (state) => state.litGroupCast.books;
export const selectCastLastResult = (state) => state.litGroupCast.lastResult;
export const selectContextByChar = (state) => state.litGroupCast.contextByChar;

// Cast partitioned for the UI: real (has dialogue) vs. suspect (zero dialogue — likely
// phantoms or spurious). Memoized so PrototypeWizard re-renders don't recompute.
export const selectCastPartitioned = createSelector([selectCast], (characters) => {
  const real = [];
  const suspect = [];
  for (const c of characters) {
    (c.dialogueCount > 0 ? real : suspect).push(c);
  }
  const byName = (a, b) =>
    String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase());
  real.sort(byName);
  suspect.sort(byName);
  return { real, suspect };
});

export default litGroupCastSlice.reducer;
